import {
  useEffect,
  useRef,
  createElement,
  DependencyList,
  useCallback,
  useMemo,
} from "react";
import { Stream } from "services-hooks/types";
import Hls from "hls.js";
import { useSetState } from "react-use";
import { AdStream } from "app/utils/audioAds";
import sendStreamError from "app/utils/errorTracker";
import { useFeaturesContext } from "app/context/features.context";

export interface HTMLMediaProps
  extends React.DetailedHTMLProps<
    React.AudioHTMLAttributes<HTMLAudioElement>,
    HTMLAudioElement
  > {}

function parseTimeRanges(ranges: TimeRanges) {
  const result: { start: number; end: number }[] = [];

  for (let i = 0; i < ranges.length; i++) {
    result.push({
      start: ranges.start(i),
      end: ranges.end(i),
    });
  }

  return result;
}

export interface HTMLMediaState {
  buffered: any[];
  loading: boolean;
  duration: number;
  paused: boolean;
  muted: boolean;
  hasError: boolean;
  time: number;
  playbackRate: number;
  volume: number;
  playing: boolean;
  streams: Stream[];
  adStreams: AdStream[];
  activeStreamIndex: number;
  activeAdStreamIndex: number;
  activeStream: Stream | null;
  activeAdStream: AdStream | null;
}
export interface HTMLMediaControls {
  play: () => Promise<void> | void;
  toggle: () => void;
  pause: () => void;
  mute: () => void;
  unmute: () => void;
  volume: (volume: number) => void;
  seek: (time: number) => void;
  setPlaybackRate: (rate: number) => void;
}

type MediaPropsWithRef = HTMLMediaProps & {
  ref?: React.MutableRefObject<HTMLAudioElement | null>;
};

interface AppAudioHookProps {
  artwork: MediaImage[] | undefined;
  title: string | undefined;
  artist: string | undefined;
  isFavorite: boolean;
  streamType: "radio" | "podcast";
  onFavoriteToggle?: (enabled: boolean) => void;
  onShuffleToggle?: (enabled: boolean) => void;
  onLoopAllToggle?: (enabled: boolean) => void;
  onLoopOneToggle?: (enabled: boolean) => void;
  onStreamEnd?: VoidFunction;
  onNextTrack?: VoidFunction;
  onSeekForward?: VoidFunction;
  onSeekBackward?: VoidFunction;
  onAdStreamEnd?: VoidFunction;
  onStop?: VoidFunction;
  onPreviousTrack?: VoidFunction;
  onPlaybackError?: (isHttps?: boolean) => void;
  audioProps: HTMLMediaProps | null;
  streamsLoading: boolean;
  streams: Stream[];
  adStreams: AdStream[];
  autoplay?: boolean;
  initialProgress?: number;
  liveStream?: boolean;
  favoritesEnabled: boolean;
  permalink?: string;
}
const initialAudioState: HTMLMediaState = {
  buffered: [],
  loading: true,
  time: 0,
  duration: 0,
  paused: true,
  hasError: false,
  muted: false,
  volume: 1,
  playbackRate: 1,
  playing: false,
  streams: [],
  adStreams: [],
  activeAdStream: null,
  activeStream: null,
  activeAdStreamIndex: 0,
  activeStreamIndex: 0,
};

export default function useAppAudio(
  {
    artwork,
    artist,
    title,
    isFavorite,
    streamType,
    onFavoriteToggle,
    onShuffleToggle,
    onLoopAllToggle,
    onLoopOneToggle,
    onStreamEnd,
    onNextTrack,
    onStop,
    onPreviousTrack,
    onSeekForward,
    onSeekBackward,
    onAdStreamEnd,
    audioProps,
    streams,
    adStreams,
    autoplay = false,
    onPlaybackError,
    favoritesEnabled,
    streamsLoading,
    initialProgress,
    liveStream = false,
    permalink,
  }: AppAudioHookProps,
  dependencies: DependencyList
) {
  const [state, setState] = useSetState<HTMLMediaState>(initialAudioState);
  const ref = useRef<HTMLAudioElement | null>(null);
  const backgroundRef = useRef<HTMLAudioElement | null>(null);
  const hls = useRef<Hls | null>(null);
  const { forceMediaSession } = useFeaturesContext();
  const wrapEvent = (userEvent: any, proxyEvent?: any) => {
    return (event: any) => {
      try {
        proxyEvent && proxyEvent(event);
      } finally {
        userEvent && userEvent(event);
      }
    };
  };

  const playBackgroundStream = () => {
    if (forceMediaSession && backgroundRef.current) {
      backgroundRef.current.play().catch(console.log);
    }
  };

  const pauseBackgroundStream = () => {
    if (forceMediaSession && backgroundRef.current) {
      backgroundRef.current.pause();
    }
  };

  const onPlay = () => {
    setState({ paused: false });
  };

  const onPlaying = () => {
    pauseBackgroundStream();
    setState({ paused: false, playing: true, loading: false });
  };

  const onWaiting = () => {
    // console.log("ON WAITING");
    playBackgroundStream();
    setState({ playing: false, loading: true });
  };

  const onPause = () => {
    setState({ paused: true, playing: false, loading: false });
  };

  const onStalled = () => {
    // if (!state.paused) {
    //   setState({ loading: true });
    // }
  };

  const onCanPlay = (e: any) => {
    // console.log("onCanPlay", e);
    // if (!state.paused) {
    //   setState({ loading: true });
    // }
  };

  const onLoadedMetadata = () => {
    if (ref.current && initialProgress !== undefined) {
      ref.current.currentTime = initialProgress;
    }
  };

  const onLoadedData = () => {
    setState({ loading: false });
  };

  const onVolumeChange = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setState({
      muted: el.muted,
      volume: el.volume,
    });
  };

  const onDurationChange = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const { duration, buffered, currentTime, playbackRate } = el;

    setState({
      duration,
    });
  };

  const onRateChange = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const { playbackRate } = el;
    setState({
      playbackRate,
    });
  };

  const onTimeUpdate = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setState({ time: el.currentTime });
  };

  const onProgress = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setState({ buffered: parseTimeRanges(el.buffered) });
  };

  const onError = useCallback(
    (e: any) => {
      console.log("onError", e);
      if (state.activeAdStream) {
        setState((prevState) => ({
          activeAdStreamIndex: prevState.activeAdStreamIndex + 1,
        }));
      } else if (state.activeStream) {
        if (
          state.streams.length > 0 &&
          state.activeStreamIndex >= state.streams.length - 1 &&
          window.navigator.onLine
        ) {
          setState({
            paused: true,
            playing: false,
            loading: false,
            hasError: true,
          });
          playBackgroundStream();
          onPlaybackError &&
            onPlaybackError(state.activeStream?.url?.startsWith("https"));
        }

        if (permalink) {
          sendStreamError({
            permalink: permalink,
            stream: state.activeStream?.url,
            error: e.type,
          });
        }

        setState((prevState) => ({
          activeStreamIndex: prevState.activeStreamIndex + 1,
        }));
      }
    },
    [state]
  );

  const playStream = (stream: Stream) => {
    if (ref.current) {
      let retryCount = 0;

      ref.current.volume = 0.7;
      setState({ loading: true });
      ref.current.pause();
      if (hls.current != null) {
        // console.log("REGULAR STREAM");
        hls.current.destroy();
      }
      if (
        Hls.isSupported() &&
        (stream.format?.includes("hls") || stream.protocol?.includes("hls"))
      ) {
        // console.log("HLS STREAM");
        var config = {
          debug: false,
        };

        hls.current = new Hls(config);
        hls.current.loadSource(stream.url!);
        hls.current.attachMedia(ref.current);
        hls.current.on(Hls.Events.MANIFEST_PARSED, () => {
          if (autoplay) {
            ref.current?.play().catch((e) => {
              console.log("HLS could not play audio", e);
            });
          }
        });
        hls.current.on(Hls.Events.ERROR, (event, data) => {
          console.log("HLS ERROR");
          console.error(event, data);
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
                onError(data);
              } else {
                if (retryCount < 3) {
                  retryCount++;
                  hls.current?.startLoad();
                } else {
                  console.log("[AUDIO] HLS retry limit reached");
                  onError(data);
                }
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.current?.recoverMediaError();
              break;
            default:
              hls.current?.destroy();
              break;
          }
        });
      } else {
        // console.log("REGULAR, autoplay:", autoplay);
        ref.current.src = stream.url!;
        // ref.current.load();
        if (autoplay) {
          controls.play();
        }
      }
    }
  };

  const onEnded = () => {
    if (state.activeAdStream) {
      setState({
        activeAdStreamIndex: 0,
        adStreams: [],
      });
      if (onAdStreamEnd) {
        onAdStreamEnd();
      }
    } else {
      setState({ paused: true, playing: false, loading: false });

      if (onStreamEnd) {
        onStreamEnd();
      }
    }
  };

  const controls: HTMLMediaControls = {
    toggle: () => {
      if (state.paused) {
        controls.play();
      } else {
        controls.pause();
      }
    },
    play: () => {
      if (state.streams && state.activeStreamIndex > state.streams.length - 1) {
        setState({ activeStreamIndex: 0, hasError: false });
      }
      if (
        !state.activeStream &&
        state.activeStreamIndex < state.streams.length
      ) {
        setState((prevState) => ({
          activeStream: prevState.streams[prevState.activeStreamIndex],
        }));
      }
      if (
        liveStream &&
        ref.current &&
        !state.activeAdStream &&
        !state.activeStream?.format?.includes("hls")
      ) {
        // ref.current.load();
      }
      const el = ref.current;
      // console.log("ABOUT TO PLAY", el);
      if (!el) {
        return undefined;
      }
      el.play()
        ?.catch((e) => console.log("PLAYBACKERROR", e))
        .then((e) => {
          console.log("PLAYING!");
        });
    },
    pause: () => {
      const el = ref.current;
      if (!el || state.duration === undefined) {
        return;
      }
      if (el) {
        return el.pause();
      }
    },
    seek: (time: number) => {
      const el = ref.current;
      if (!el || state.duration === undefined) {
        return;
      }
      time = Math.min(state.duration, Math.max(0, time));
      el.currentTime = time;
    },
    volume: (volume: number) => {
      const el = ref.current;
      if (!el) {
        return;
      }
      volume = Math.min(1, Math.max(0, volume));
      el.volume = volume;
      setState({ volume });
    },
    setPlaybackRate: (newPlaybackRate: number) => {
      const el = ref.current;
      if (!el) {
        return;
      }
      if (newPlaybackRate < 2 && newPlaybackRate > 0) {
        el.playbackRate = newPlaybackRate;
      }
      setState({ playbackRate: newPlaybackRate });
    },
    mute: () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      el.muted = true;
    },
    unmute: () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      el.muted = false;
    },
  };

  useEffect(() => {
    if ("mediaSession" in navigator) {
      if (streamType === "radio") {
        try {
          navigator.mediaSession.setPositionState({
            duration: Infinity,
            playbackRate: 1,
            position: 0,
          });
        } catch (error) {
          console.log("setPositionState error");
        }
      }
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: title,
          artist: artist,
          artwork: artwork,
          type: streamType,
          favorite: isFavorite,
          favoriteEnabled: favoritesEnabled,
        } as any);
      } catch (e) {
        console.error(e);
        try {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: title,
            artist: artist,
            artwork: artwork,
          });
        } catch (e) {
          console.error(e);
        }
        try {
          (navigator.mediaSession.metadata as any).type = streamType;
        } catch (e) {
          console.error(e);
        }
        try {
          (navigator.mediaSession.metadata as any).favorite = isFavorite;
        } catch (e) {
          console.error(e);
        }
        try {
          (navigator.mediaSession.metadata as any).favoriteEnabled =
            favoritesEnabled;
        } catch (e) {
          console.error(e);
        }
      }

      try {
        if (!state.loading) {
          navigator.mediaSession.playbackState = state.playing
            ? "playing"
            : "paused";
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [
    title,
    artist,
    artwork,
    streamType,
    favoritesEnabled,
    isFavorite,
    state.playing,
    state.loading,
  ]);

  useEffect(() => {
    if ("mediaSession" in navigator) {
      try {
        (navigator.mediaSession.metadata as any).playbackError = state.hasError;
      } catch (e) {
        console.error(e);
      }
    }
  }, [state.hasError]);

  useEffect(() => {
    if ("mediaSession" in navigator) {
      try {
        (navigator.mediaSession.metadata as any).loading =
          state.loading || streamsLoading;
      } catch (e) {
        console.error(e);
      }
    }
  }, [state.loading]);

  const onMediaSessionPause = useCallback(() => {
    if (!state.loading) {
      controls.pause();
    }
  }, [state.loading]);

  const onMediaSessionPlay = useCallback(() => {
    if (!state.loading) {
      controls.play();
    }
  }, [state.loading]);
  const mediaSessionDependencies = useMemo(
    () => [
      ...dependencies,
      onMediaSessionPause,
      onMediaSessionPlay,
      onPreviousTrack,
      onFavoriteToggle,
      onLoopOneToggle,
      onLoopAllToggle,
      onShuffleToggle,
    ],
    [
      dependencies,
      onMediaSessionPause,
      onMediaSessionPlay,
      onPreviousTrack,
      onNextTrack,
      onFavoriteToggle,
      onLoopOneToggle,
      onLoopAllToggle,
      onShuffleToggle,
    ]
  );
  useEffect(() => {
    if ("mediaSession" in navigator) {
      try {
        navigator.mediaSession.setActionHandler("play", onMediaSessionPlay);
      } catch (e) {
        console.error(e);
      }

      try {
        navigator.mediaSession.setActionHandler("pause", onMediaSessionPause);
      } catch (e) {
        console.error(e);
      }

      if (streamType === "podcast") {
        try {
          navigator.mediaSession.setActionHandler("seekbackward", () => {
            onSeekBackward && onSeekBackward();
          });
        } catch (e) {
          console.error(e);
        }

        try {
          navigator.mediaSession.setActionHandler("seekforward", () => {
            onSeekForward && onSeekForward();
          });
        } catch (e) {
          console.error(e);
        }
      } else {
        try {
          navigator.mediaSession.setActionHandler("seekbackward", null);
        } catch (e) {
          console.error(e);
        }

        try {
          navigator.mediaSession.setActionHandler("seekforward", null);
        } catch (e) {
          console.error(e);
        }
      }

      try {
        navigator.mediaSession.setActionHandler("stop", () => {
          controls.pause();
          onStop && onStop();
        });
      } catch (e) {
        console.error(e);
      }

      try {
        navigator.mediaSession.setActionHandler(
          "previoustrack",
          onPreviousTrack || null
        );
      } catch (e) {
        console.error(e);
      }

      try {
        navigator.mediaSession.setActionHandler(
          "nexttrack",
          onNextTrack || null
        );
      } catch (e) {
        console.error(e);
      }

      try {
        navigator.mediaSession.setActionHandler(
          "favorite" as any,
          (event: any) => {
            onFavoriteToggle && onFavoriteToggle(event.enable);
          }
        );

        navigator.mediaSession.setActionHandler(
          "shuffle" as any,
          (event: any) => {
            onShuffleToggle && onShuffleToggle(event.enable);
          }
        );

        navigator.mediaSession.setActionHandler(
          "loopone" as any,
          (event: any) => {
            try {
              onLoopOneToggle && onLoopOneToggle(event.enabled);
            } catch (e) {}
          }
        );

        navigator.mediaSession.setActionHandler(
          "loopall" as any,
          (event: any) => {
            try {
              onLoopAllToggle && onLoopAllToggle(event.enable);
            } catch (e) {}
          }
        );
      } catch (e) {}
    }
  }, mediaSessionDependencies);
  useEffect(() => {
    controls.pause();
    setState(initialAudioState);
  }, dependencies);

  useEffect(() => {
    if (!streamsLoading) {
      setState({ streams, adStreams });
    }
  }, [streams, adStreams, streamsLoading]);

  useEffect(() => {
    setState((prevState) => ({
      activeStream: prevState.streams[prevState.activeStreamIndex],
      activeAdStream: prevState.adStreams[prevState.activeAdStreamIndex],
    }));
  }, [
    state.streams,
    state.adStreams,
    state.activeAdStreamIndex,
    state.activeStreamIndex,
  ]);

  useEffect(() => {
    if (state.activeAdStream) {
      playStream(state.activeAdStream.stream);
    } else if (state.activeStream) {
      playStream(state.activeStream);
    }
  }, [state.activeStream, state.activeAdStream]);

  const audioElement: React.ReactElement<MediaPropsWithRef> = createElement(
    "audio",
    {
      controls: false,
      ...audioProps,
      ref,
      onEnded: wrapEvent(audioProps?.onEnded, onEnded),
      onPlay: wrapEvent(audioProps?.onPlay, onPlay),
      onPlaying: wrapEvent(audioProps?.onPlaying, onPlaying),
      onWaiting: wrapEvent(audioProps?.onWaiting, onWaiting),
      onPause: wrapEvent(audioProps?.onPause, onPause),
      onError: wrapEvent(audioProps?.onError, onError),
      onStalled: wrapEvent(audioProps?.onStalled, onStalled),
      onLoadedMetadata: wrapEvent(
        audioProps?.onLoadedMetadata,
        onLoadedMetadata
      ),
      onCanPlay: wrapEvent(audioProps?.onCanPlay, onCanPlay),
      onLoadedData: wrapEvent(audioProps?.onLoadedData, onLoadedData),
      onVolumeChange: wrapEvent(audioProps?.onVolumeChange, onVolumeChange),
      onDurationChange: wrapEvent(
        audioProps?.onDurationChange,
        onDurationChange
      ),
      onRateChange: wrapEvent(audioProps?.onRateChange, onRateChange),
      onTimeUpdate: wrapEvent(audioProps?.onTimeUpdate, onTimeUpdate),
      onProgress: wrapEvent(audioProps?.onProgress, onProgress),
    } as any
  );

  const fallbackAudioElement: React.ReactElement<MediaPropsWithRef> | null =
    forceMediaSession
      ? createElement("audio", {
          controls: false,
          loop: true,
          ref: backgroundRef,
          src: "/10-seconds-of-silence.mp3",
        } as any)
      : null;
  return {
    audioElement,
    fallbackAudioElement,
    state,
    controls,
    audioRef: ref,
  } as const;
}
